import Typography from "antd/lib/typography";
import clsx from "clsx";
import { type ReactNode } from "react";
import { useTranslation } from "react-i18next";

import { Feature, useCheckFeatureFlag } from "@sol/features";
import { getUserDisplayName, type INotificationResource, parseUtcDate } from "@sol/sdk";

import useLocale from "@/shared/hooks/useLocale";
import { formatTimeSince } from "@/shared/lib/date";

import { NotificationMessage } from "./NotificationMessage";
import { getNotificationMessage } from "../lib/getNotificationMessage";
import { isNotificationUnread } from "../lib/isNotificationUnread";
import { isSenderShownAsTrainer } from "../lib/isSenderShownAsTrainer";

type Props = {
    notification: INotificationResource;
    avatar: ReactNode;
    actions?: ReactNode;
    onOpen: (notification: INotificationResource) => void;
};

export const NotificationItem = ({ notification, avatar, actions, onOpen }: Props) => {
    const [t] = useTranslation();
    const [locale] = useLocale();
    const checkFeatureFlag = useCheckFeatureFlag();

    const isUnread = isNotificationUnread(notification);
    const senderName = isSenderShownAsTrainer(notification.type, checkFeatureFlag(Feature.WORKSPACE_GROUP_SUBMIT))
        ? t("navbar.trainer")
        : getUserDisplayName(notification.senderUser);
    const segments = getNotificationMessage(notification, {
        isLearnerFollowUpWording: checkFeatureFlag(Feature.FOLLOW_UPS_NOTIFICATIONS_LEARNER),
    });

    return (
        <div
            className={clsx(
                "group flex items-start transition-colors duration-200",
                isUnread ? "bg-ant-primary hover:bg-ant-primary-hover" : "bg-transparent hover:bg-ant-fill-tertiary",
            )}
        >
            <button
                type="button"
                onClick={() => onOpen(notification)}
                className={clsx(
                    "flex min-w-0 flex-1 items-start gap-ant-sm py-ant-sm pl-ant text-left",
                    "focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-primary",
                    actions ? "pr-ant-xs" : "pr-ant",
                )}
                data-cy="notification-item"
            >
                <span aria-hidden className={clsx("mt-3.5 size-1.5 shrink-0 rounded-full", isUnread && "bg-primary")} />
                <span aria-hidden className="flex shrink-0">
                    {avatar}
                </span>
                <span className="flex min-w-0 flex-col gap-ant-xxs">
                    <Typography.Text className="line-clamp-3">
                        {isUnread && <span className="sr-only">{t("navbar.unread")} </span>}
                        <NotificationMessage senderName={senderName} segments={segments} />
                    </Typography.Text>
                    <Typography.Text type="secondary" className="text-ant-sm">
                        {formatTimeSince(parseUtcDate(notification.createdAt), {
                            now: new Date(),
                            locale,
                            justNowLabel: t("navbar.justNow"),
                        })}
                    </Typography.Text>
                </span>
            </button>
            {actions && (
                // Touch screens cannot hover, so actions only hide behind hover for fine pointers
                <div
                    className={clsx(
                        "flex shrink-0 flex-col gap-ant-xxs py-ant-sm pr-ant-xs",
                        "pointer-fine:opacity-0 pointer-fine:group-focus-within:opacity-100 pointer-fine:group-hover:opacity-100",
                    )}
                >
                    {actions}
                </div>
            )}
        </div>
    );
};
